/**
 * 🎯 REFERRAL PROGRAM ADMIN - Manage multiple referral campaigns, generate codes, and track usage
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Menu,
  Divider,
  Paper,
  Badge,
  Switch,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Add,
  Edit,
  Visibility,
  PlayArrow,
  Pause,
  TrendingUp,
  Campaign,
  CheckCircle,
  MonetizationOn,
  People,
  MoreVert,
  Refresh,
  Code as CodeIcon,
  ContentCopy,
  Settings as SettingsIcon,
  Save
} from '@mui/icons-material';
import {
  referralProgramService,
  type ReferralProgram,
  type ReferralCode,
  type CodeUsage,
  type CreateProgramRequest
} from '@/services/referralProgramService';
import toast from 'react-hot-toast';
import dynamicContentService from '@/services/dynamicContentService';

type TabType = 'programs' | 'codes' | 'usage' | 'settings';

interface InviteSettings {
  enabled: boolean;
  inviteOnlyMode: boolean;
  defaultReferralQuota: number;
  allowUnlimitedInvites: boolean;
  requireAdminApproval: boolean;
  autoGenerateReferralCode: boolean;
}

export default function ReferralProgramAdminPage() {
  // State management
  const [activeTab, setActiveTab] = useState<TabType>('programs');
  const [programs, setPrograms] = useState<ReferralProgram[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<ReferralProgram | null>(null);
  const [codes, setCodes] = useState<ReferralCode[]>([]);
  const [codeUsage, setCodeUsage] = useState<CodeUsage[]>([]);
  const [selectedCode, setSelectedCode] = useState<ReferralCode | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Invite system settings
  const [inviteSettings, setInviteSettings] = useState<InviteSettings>({
    enabled: true,
    inviteOnlyMode: false,
    defaultReferralQuota: 5,
    allowUnlimitedInvites: false,
    requireAdminApproval: false,
    autoGenerateReferralCode: true
  });
  const [loadingBetaSettings, setLoadingBetaSettings] = useState(false);

  // Dialog states
  const [createProgramDialogOpen, setCreateProgramDialogOpen] = useState(false);
  const [editProgramDialogOpen, setEditProgramDialogOpen] = useState(false);
  const [generateCodesDialogOpen, setGenerateCodesDialogOpen] = useState(false);
  const [viewUsageDialogOpen, setViewUsageDialogOpen] = useState(false);

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuProgram, setMenuProgram] = useState<ReferralProgram | null>(null);

  // Form states
  const [programForm, setProgramForm] = useState<CreateProgramRequest>({
    name: '',
    description: '',
    codePrefix: '',
    rewards: {
      referrerAmount: 50,
      referredAmount: 50,
      currency: 'USD'
    },
    conditions: {
      minBookingAmount: 100,
      maxRedemptionsPerUser: 999,
      requiresFirstBooking: true,
      validityDays: 365
    },
    targeting: {
      userRoles: ['all']
    },
    dateRange: {
      startDate: new Date().toISOString().split('T')[0]
    }
  });

  const [generateCodesForm, setGenerateCodesForm] = useState({
    count: 10,
    autoGenerate: true
  });

  useEffect(() => {
    fetchPrograms();
    loadBetaSettings();
  }, []);

  const loadBetaSettings = async () => {
    setLoadingBetaSettings(true);
    try {
      const result = await dynamicContentService.getBetaSettings();
      if (result.success && result.data) {
        setInviteSettings(result.data);
      }
    } catch (error) {
      console.error('Error loading beta settings:', error);
    } finally {
      setLoadingBetaSettings(false);
    }
  };

  useEffect(() => {
    if (selectedProgram && activeTab === 'codes') {
      fetchProgramCodes(selectedProgram.id);
    }
  }, [selectedProgram, activeTab]);

  const fetchPrograms = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await referralProgramService.getAllPrograms();
      if (response.success) {
        setPrograms(response.data.programs);
        if (response.data.programs.length > 0 && !selectedProgram) {
          setSelectedProgram(response.data.programs[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
      setError('Failed to load referral programs');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProgramCodes = async (programId: string) => {
    try {
      const response = await referralProgramService.getProgramCodes(programId);
      if (response.success) {
        setCodes(response.data.codes);
      }
    } catch (error) {
      console.error('Error fetching codes:', error);
    }
  };

  const fetchCodeUsage = async (codeId: string) => {
    try {
      const response = await referralProgramService.getCodeUsage(codeId);
      if (response.success) {
        setCodeUsage(response.data.usage);
      }
    } catch (error) {
      console.error('Error fetching code usage:', error);
    }
  };

  const handleCreateProgram = async () => {
    try {
      const response = await referralProgramService.createProgram(programForm);
      if (response.success) {
        toast.success('Program created successfully!');
        setCreateProgramDialogOpen(false);
        resetProgramForm();
        await fetchPrograms();
      } else {
        toast.error(response.error || 'Failed to create program');
      }
    } catch (error) {
      console.error('Error creating program:', error);
      toast.error('Failed to create program');
    }
  };

  const handleUpdateProgram = async () => {
    if (!menuProgram) return;

    try {
      const response = await referralProgramService.updateProgram(menuProgram.id, programForm);
      if (response.success) {
        toast.success('Program updated successfully!');
        setEditProgramDialogOpen(false);
        resetProgramForm();
        await fetchPrograms();
      } else {
        toast.error(response.error || 'Failed to update program');
      }
    } catch (error) {
      console.error('Error updating program:', error);
      toast.error('Failed to update program');
    }
  };

  const handleGenerateCodes = async () => {
    if (!selectedProgram) return;

    try {
      const response = await referralProgramService.generateCodes(
        selectedProgram.id,
        generateCodesForm
      );

      if (response.success) {
        toast.success(`Generated ${response.data?.generated || generateCodesForm.count} codes successfully!`);
        setGenerateCodesDialogOpen(false);
        await fetchProgramCodes(selectedProgram.id);
      } else {
        toast.error(response.error || 'Failed to generate codes');
      }
    } catch (error) {
      console.error('Error generating codes:', error);
      toast.error('Failed to generate codes');
    }
  };

  const handleToggleStatus = async (program: ReferralProgram) => {
    const newStatus = program.status === 'active' ? 'paused' : 'active';

    try {
      const response = await referralProgramService.toggleProgramStatus(program.id, newStatus);
      if (response.success) {
        toast.success(`Program ${newStatus === 'active' ? 'activated' : 'paused'} successfully!`);
        await fetchPrograms();
      } else {
        toast.error(response.error || 'Failed to toggle status');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to toggle status');
    }
  };

  const resetProgramForm = () => {
    setProgramForm({
      name: '',
      description: '',
      codePrefix: '',
      rewards: {
        referrerAmount: 50,
        referredAmount: 50,
        currency: 'USD'
      },
      conditions: {
        minBookingAmount: 100,
        maxRedemptionsPerUser: 999,
        requiresFirstBooking: true,
        validityDays: 365
      },
      targeting: {
        userRoles: ['all']
      },
      dateRange: {
        startDate: new Date().toISOString().split('T')[0]
      }
    });
  };

  const openEditDialog = (program: ReferralProgram) => {
    setMenuProgram(program);
    setProgramForm({
      name: program.name,
      description: program.description,
      codePrefix: program.codePrefix,
      rewards: program.rewards,
      conditions: program.conditions,
      targeting: program.targeting,
      dateRange: program.dateRange
    });
    setEditProgramDialogOpen(true);
    setMenuAnchor(null);
  };

  const handleViewCodeUsage = (code: ReferralCode) => {
    setSelectedCode(code);
    fetchCodeUsage(code.id);
    setViewUsageDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'ended': return 'error';
      case 'completed': return 'success';
      case 'registered': return 'info';
      case 'pending': return 'warning';
      case 'expired': return 'error';
      case 'paid': return 'success';
      case 'earned': return 'info';
      default: return 'default';
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle }: any) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
              {typeof value === 'number' && title.includes('Rate')
                ? `${(value * 100).toFixed(1)}%`
                : typeof value === 'number' && (title.includes('$') || title.includes('Paid'))
                ? `$${value.toLocaleString()}`
                : value?.toLocaleString() || '0'
              }
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <LinearProgress sx={{ width: 200 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
            Referral Program Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage multiple referral campaigns, generate codes, and track usage
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchPrograms}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateProgramDialogOpen(true)}
          >
            Create Program
          </Button>
        </Stack>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            label={`Programs (${programs.length})`}
            value="programs"
            icon={<Campaign />}
            iconPosition="start"
          />
          <Tab
            label={`REF CODES (${codes.length})`}
            value="codes"
            icon={<CodeIcon />}
            iconPosition="start"
            disabled={!selectedProgram}
          />
          <Tab
            label="Usage Tracking"
            value="usage"
            icon={<People />}
            iconPosition="start"
            disabled={!selectedCode}
          />
          <Tab
            label="Invite System"
            value="settings"
            icon={<SettingsIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Card>

      {/* Programs Tab */}
      {activeTab === 'programs' && (
        <Box>
          {/* Stats Overview */}
          {selectedProgram && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Codes"
                  value={selectedProgram.stats.totalCodes}
                  icon={<CodeIcon sx={{ color: 'white' }} />}
                  color="primary.main"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Completed Referrals"
                  value={selectedProgram.stats.completedReferrals}
                  icon={<CheckCircle sx={{ color: 'white' }} />}
                  color="success.main"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="$ Rewards Paid"
                  value={selectedProgram.stats.totalRewardsPaid}
                  icon={<MonetizationOn sx={{ color: 'white' }} />}
                  color="warning.main"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Conversion Rate"
                  value={selectedProgram.stats.conversionRate}
                  icon={<TrendingUp sx={{ color: 'white' }} />}
                  color="info.main"
                />
              </Grid>
            </Grid>
          )}

          {/* Programs Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                All Referral Programs
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Program Name</TableCell>
                      <TableCell>Code Prefix</TableCell>
                      <TableCell>Rewards</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Performance</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {programs.map((program) => (
                      <TableRow
                        key={program.id}
                        hover
                        selected={selectedProgram?.id === program.id}
                        onClick={() => setSelectedProgram(program)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {program.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {program.description}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={program.codePrefix}
                            size="small"
                            variant="outlined"
                            icon={<CodeIcon />}
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              Referrer: ${program.rewards.referrerAmount}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Referred: ${program.rewards.referredAmount}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={program.status}
                            size="small"
                            color={getStatusColor(program.status) as any}
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {program.stats.completedReferrals} / {program.stats.totalReferrals} completed
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ${program.stats.totalRewardsPaid.toLocaleString()} paid
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Tooltip title={program.status === 'active' ? 'Pause' : 'Activate'}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleStatus(program);
                                }}
                              >
                                {program.status === 'active' ? <Pause /> : <PlayArrow />}
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuProgram(program);
                                setMenuAnchor(e.currentTarget);
                              }}
                            >
                              <MoreVert />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {programs.length === 0 && (
                <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'action.hover', mt: 2 }}>
                  <Campaign sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    No referral programs yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Create your first referral program to start tracking referrals
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setCreateProgramDialogOpen(true)}
                  >
                    Create Program
                  </Button>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Codes Tab */}
      {activeTab === 'codes' && selectedProgram && (
        <Box>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Referral Codes for: {selectedProgram.name}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={`Prefix: ${selectedProgram.codePrefix}`}
                      size="small"
                      variant="outlined"
                      icon={<CodeIcon />}
                    />
                    <Chip
                      label={`${codes.length} codes generated`}
                      size="small"
                      color="primary"
                    />
                    <Chip
                      label={`${codes.filter(c => c.status === 'active').length} active`}
                      size="small"
                      color="success"
                    />
                  </Stack>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setGenerateCodesDialogOpen(true)}
                >
                  Generate Codes
                </Button>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Owner</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Usage</TableCell>
                      <TableCell>Last Used</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {codes.map((code) => (
                      <TableRow key={code.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                              {code.code}
                            </Typography>
                            <Tooltip title="Copy code">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  navigator.clipboard.writeText(code.code);
                                  toast.success('Code copied!');
                                }}
                              >
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">{code.ownerName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {code.ownerEmail}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={code.status}
                            size="small"
                            color={getStatusColor(code.status) as any}
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Badge badgeContent={code.usageCount} color="primary">
                            <Box sx={{ width: 40 }} />
                          </Badge>
                          {code.maxUsage && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              / {code.maxUsage}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {code.lastUsedAt
                              ? new Date(code.lastUsedAt).toLocaleDateString()
                              : 'Never'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View usage details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewCodeUsage(code)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {codes.length === 0 && (
                <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'action.hover', mt: 2 }}>
                  <CodeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    No codes generated yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Generate referral codes for this program
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setGenerateCodesDialogOpen(true)}
                  >
                    Generate Codes
                  </Button>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Usage Tracking Tab */}
      {activeTab === 'usage' && selectedCode && (
        <Box>
          {/* Usage Stats */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Uses"
                value={selectedCode.usageCount}
                icon={<People sx={{ color: 'white' }} />}
                color="primary.main"
                subtitle={selectedCode.maxUsage ? `Limit: ${selectedCode.maxUsage}` : 'Unlimited'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Completed"
                value={codeUsage.filter(u => u.status === 'completed').length}
                icon={<CheckCircle sx={{ color: 'white' }} />}
                color="success.main"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Rewards"
                value={codeUsage.reduce((sum, u) => sum + (u.status === 'completed' ? u.rewardAmount : 0), 0)}
                icon={<MonetizationOn sx={{ color: 'white' }} />}
                color="warning.main"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Pending"
                value={codeUsage.filter(u => u.status === 'pending' || u.status === 'registered').length}
                icon={<TrendingUp sx={{ color: 'white' }} />}
                color="info.main"
              />
            </Grid>
          </Grid>

          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Usage Details for: {selectedCode.code}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Owner: {selectedCode.ownerName} ({selectedCode.ownerEmail})
                  </Typography>
                </Box>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Referred User</TableCell>
                      <TableCell>Date Used</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Booking</TableCell>
                      <TableCell>Reward</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {codeUsage.map((usage) => (
                      <TableRow key={usage.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {usage.referredUserName || 'Pending'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {usage.referredUserEmail || usage.referredUserPhone}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {new Date(usage.usedAt).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={usage.status}
                            size="small"
                            color={getStatusColor(usage.status) as any}
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>
                          {usage.bookingId ? (
                            <Box>
                              <Typography variant="body2">{usage.bookingId}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                ${usage.bookingAmount}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No booking yet
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              ${usage.rewardAmount}
                            </Typography>
                            <Chip
                              label={usage.rewardStatus}
                              size="small"
                              color={getStatusColor(usage.rewardStatus) as any}
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {codeUsage.length === 0 && (
                <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'action.hover', mt: 2 }}>
                  <People sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    No usage yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This code hasn't been used by anyone yet
                  </Typography>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Settings/Invite System Tab */}
      {activeTab === 'settings' && (
        <Card>
          <CardContent>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Beta Access Control
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage invite-only mode and referral quota settings
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Invite-Only Mode Toggle */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3, bgcolor: inviteSettings.inviteOnlyMode ? 'primary.50' : 'background.paper' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={inviteSettings.inviteOnlyMode}
                        onChange={(e) => setInviteSettings({ ...inviteSettings, inviteOnlyMode: e.target.checked })}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          Invite-Only Mode
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          When enabled, only whitelisted users or those with referral codes can register
                        </Typography>
                      </Box>
                    }
                  />
                </Paper>
              </Grid>

              {/* Default Referral Quota */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Default Referral Quota"
                  value={inviteSettings.defaultReferralQuota}
                  onChange={(e) => setInviteSettings({ ...inviteSettings, defaultReferralQuota: parseInt(e.target.value) })}
                  helperText="How many people each user can invite"
                  InputProps={{ inputProps: { min: 1, max: 100 } }}
                />
              </Grid>

              {/* Allow Unlimited Invites */}
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={inviteSettings.allowUnlimitedInvites}
                      onChange={(e) => setInviteSettings({ ...inviteSettings, allowUnlimitedInvites: e.target.checked })}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Allow Unlimited Invites
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Remove invite limits for all users
                      </Typography>
                    </Box>
                  }
                />
              </Grid>

              {/* Auto-Generate Referral Code */}
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={inviteSettings.autoGenerateReferralCode}
                      onChange={(e) => setInviteSettings({ ...inviteSettings, autoGenerateReferralCode: e.target.checked })}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Auto-Generate Referral Codes
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Automatically create referral code for new users
                      </Typography>
                    </Box>
                  }
                />
              </Grid>

              {/* Require Admin Approval */}
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={inviteSettings.requireAdminApproval}
                      onChange={(e) => setInviteSettings({ ...inviteSettings, requireAdminApproval: e.target.checked })}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Require Admin Approval
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        New registrations need admin approval
                      </Typography>
                    </Box>
                  }
                />
              </Grid>

              {/* Save Button */}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Save />}
                  disabled={loadingBetaSettings}
                  onClick={async () => {
                    try {
                      setLoadingBetaSettings(true);
                      const result = await dynamicContentService.updateBetaSettings(inviteSettings);
                      if (result.success) {
                        toast.success('Invite system settings saved successfully!');
                      } else {
                        toast.error(result.error || 'Failed to save settings');
                      }
                    } catch (error: any) {
                      console.error('Error saving beta settings:', error);
                      toast.error(error.response?.data?.error || 'Failed to save settings');
                    } finally {
                      setLoadingBetaSettings(false);
                    }
                  }}
                  sx={{ mt: 2 }}
                >
                  {loadingBetaSettings ? 'Saving...' : 'Save Settings'}
                </Button>
              </Grid>

              {/* Info Alert */}
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Note:</strong> When invite-only mode is enabled, new users must either be whitelisted by admins
                    in the Beta Invitations page, or use a valid referral code from an existing user.
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Program Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => openEditDialog(menuProgram!)}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Edit Program
        </MenuItem>
        <MenuItem onClick={() => {
          setSelectedProgram(menuProgram);
          setActiveTab('codes');
          setMenuAnchor(null);
        }}>
          <CodeIcon fontSize="small" sx={{ mr: 1 }} />
          View Codes
        </MenuItem>
      </Menu>

      {/* Create Program Dialog */}
      <Dialog open={createProgramDialogOpen} onClose={() => setCreateProgramDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Referral Program</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Program Name"
                value={programForm.name}
                onChange={(e) => setProgramForm({ ...programForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={programForm.description}
                onChange={(e) => setProgramForm({ ...programForm, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Code Prefix"
                value={programForm.codePrefix}
                onChange={(e) => setProgramForm({ ...programForm, codePrefix: e.target.value.toUpperCase() })}
                helperText="e.g., SUMMER, HOST, VIP"
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                type="number"
                label="Referrer Reward ($)"
                value={programForm.rewards.referrerAmount}
                onChange={(e) => setProgramForm({
                  ...programForm,
                  rewards: { ...programForm.rewards, referrerAmount: parseFloat(e.target.value) }
                })}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                type="number"
                label="Referred Reward ($)"
                value={programForm.rewards.referredAmount}
                onChange={(e) => setProgramForm({
                  ...programForm,
                  rewards: { ...programForm.rewards, referredAmount: parseFloat(e.target.value) }
                })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Min Booking Amount ($)"
                value={programForm.conditions.minBookingAmount}
                onChange={(e) => setProgramForm({
                  ...programForm,
                  conditions: { ...programForm.conditions, minBookingAmount: parseFloat(e.target.value) }
                })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Validity (Days)"
                value={programForm.conditions.validityDays}
                onChange={(e) => setProgramForm({
                  ...programForm,
                  conditions: { ...programForm.conditions, validityDays: parseInt(e.target.value) }
                })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={programForm.dateRange.startDate}
                onChange={(e) => setProgramForm({
                  ...programForm,
                  dateRange: { ...programForm.dateRange, startDate: e.target.value }
                })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date (Optional)"
                value={programForm.dateRange.endDate || ''}
                onChange={(e) => setProgramForm({
                  ...programForm,
                  dateRange: { ...programForm.dateRange, endDate: e.target.value }
                })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={programForm.conditions.requiresFirstBooking}
                    onChange={(e) => setProgramForm({
                      ...programForm,
                      conditions: { ...programForm.conditions, requiresFirstBooking: e.target.checked }
                    })}
                  />
                }
                label="Require first booking for reward"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateProgramDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateProgram}
            disabled={!programForm.name || !programForm.codePrefix}
          >
            Create Program
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Program Dialog */}
      <Dialog open={editProgramDialogOpen} onClose={() => setEditProgramDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Program: {menuProgram?.name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Program Name"
                value={programForm.name}
                onChange={(e) => setProgramForm({ ...programForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={programForm.description}
                onChange={(e) => setProgramForm({ ...programForm, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Code Prefix"
                value={programForm.codePrefix}
                onChange={(e) => setProgramForm({ ...programForm, codePrefix: e.target.value.toUpperCase() })}
                helperText="e.g., SUMMER, HOST, VIP"
                disabled
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                type="number"
                label="Referrer Reward ($)"
                value={programForm.rewards.referrerAmount}
                onChange={(e) => setProgramForm({
                  ...programForm,
                  rewards: { ...programForm.rewards, referrerAmount: parseFloat(e.target.value) }
                })}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                type="number"
                label="Referred Reward ($)"
                value={programForm.rewards.referredAmount}
                onChange={(e) => setProgramForm({
                  ...programForm,
                  rewards: { ...programForm.rewards, referredAmount: parseFloat(e.target.value) }
                })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Min Booking Amount ($)"
                value={programForm.conditions.minBookingAmount}
                onChange={(e) => setProgramForm({
                  ...programForm,
                  conditions: { ...programForm.conditions, minBookingAmount: parseFloat(e.target.value) }
                })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Validity (Days)"
                value={programForm.conditions.validityDays}
                onChange={(e) => setProgramForm({
                  ...programForm,
                  conditions: { ...programForm.conditions, validityDays: parseInt(e.target.value) }
                })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={programForm.dateRange.startDate}
                onChange={(e) => setProgramForm({
                  ...programForm,
                  dateRange: { ...programForm.dateRange, startDate: e.target.value }
                })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date (Optional)"
                value={programForm.dateRange.endDate || ''}
                onChange={(e) => setProgramForm({
                  ...programForm,
                  dateRange: { ...programForm.dateRange, endDate: e.target.value }
                })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={programForm.conditions.requiresFirstBooking}
                    onChange={(e) => setProgramForm({
                      ...programForm,
                      conditions: { ...programForm.conditions, requiresFirstBooking: e.target.checked }
                    })}
                  />
                }
                label="Require first booking for reward"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditProgramDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateProgram}
            disabled={!programForm.name || !programForm.codePrefix}
          >
            Update Program
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Codes Dialog */}
      <Dialog open={generateCodesDialogOpen} onClose={() => setGenerateCodesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Referral Codes</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Generate codes for: <strong>{selectedProgram?.name}</strong>
            </Typography>
            <TextField
              fullWidth
              type="number"
              label="Number of Codes"
              value={generateCodesForm.count}
              onChange={(e) => setGenerateCodesForm({
                ...generateCodesForm,
                count: parseInt(e.target.value)
              })}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={generateCodesForm.autoGenerate}
                  onChange={(e) => setGenerateCodesForm({
                    ...generateCodesForm,
                    autoGenerate: e.target.checked
                  })}
                />
              }
              label="Auto-generate unique codes"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateCodesDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleGenerateCodes}
            disabled={generateCodesForm.count < 1}
          >
            Generate {generateCodesForm.count} Codes
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Usage Dialog */}
      <Dialog open={viewUsageDialogOpen} onClose={() => setViewUsageDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Code Usage: {selectedCode?.code}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Owner: <strong>{selectedCode?.ownerName}</strong> ({selectedCode?.ownerEmail})
            </Typography>
            <Divider sx={{ my: 2 }} />
            {codeUsage.length > 0 ? (
              <Box>
                {codeUsage.map((usage) => (
                  <Paper key={usage.id} sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Referred User</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {usage.referredUserName || 'Pending Registration'}
                        </Typography>
                        <Typography variant="caption">{usage.referredUserEmail}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            label={usage.status}
                            size="small"
                            color={getStatusColor(usage.status) as any}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Used On</Typography>
                        <Typography variant="body2">
                          {new Date(usage.usedAt).toLocaleDateString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Reward</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          ${usage.rewardAmount}
                        </Typography>
                        <Chip
                          label={usage.rewardStatus}
                          size="small"
                          color={getStatusColor(usage.rewardStatus) as any}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No usage recorded for this code yet
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewUsageDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
